import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { TextArea } from "@progress/kendo-react-inputs";
import { useCallback, useEffect, useState } from "react";
import { IDNameViewModel } from "TypeGen/id-name-view-model";
import { JsonResponse } from "TypeGen/json-response";
import { fetchApi } from "../../services/api";
import { executionEventOptions, executionEventTypeValues, ExecutionEventOption } from "./Scorecard.constants";

type DriverScoreDispute = {
  DriverScoreEventID: number;
  DriverID: number;
  EventType: number;
  EventTypeLabel: string;
  Description: string;
  ScoreBefore: number;
  ScoreChange: number;
  ScoreAfter: number;
  IsUndone: boolean;
  DisputeStatus: number | null;
  DisputeNote: string;
  DisputeResolutionNote: string;
  QuoteID?: number;
  OrderID?: number;
  SamsaraSafetyEventId?: string;

  ReasonsLate: IDNameViewModel[];
};

const DisputeStatus = {
  Pending: 100,
  ScoreAdjusted: 120,
  ScoreStands: 130,
} as const;

const formatScoreChange = (value: number) => {
  if (value > 0) return `+${value.toFixed(2)}`;
  if (value < 0) return value.toFixed(2);
  return "";
};

const requiresReasonLateSelection = (currentEventType: number, selectedEventTypeId: number | null) => {
  if (selectedEventTypeId === null || currentEventType === selectedEventTypeId) {
    return false;
  }

  return executionEventTypeValues.has(currentEventType) && executionEventTypeValues.has(selectedEventTypeId);
};

type Props = {
  disputeId: number | null;
  onUpdated: () => void;
};

const ScoreDisputeDetails = ({ disputeId, onUpdated }: Props) => {
  const [dispute, setDispute] = useState<DriverScoreDispute | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingEventType, setSavingEventType] = useState(false);
  const [resolutionNote, setResolutionNote] = useState("");
  const [selectedStatusId, setSelectedStatusId] = useState<number | null>(null);
  const [editingEventTypeId, setEditingEventTypeId] = useState<number | null>(null);
  const [reasonLateOptions, setReasonLateOptions] = useState<IDNameViewModel[]>([]);
  const [selectedReasonLateId, setSelectedReasonLateId] = useState<number>(0);

  const resetState = () => {
    setDispute(null);
    setResolutionNote("");
    setEditingEventTypeId(null);
    setSelectedReasonLateId(0);
  };

  const loadDispute = useCallback(() => {
    if (!disputeId) {
      resetState();
      return;
    }

    setLoading(true);
    fetchApi(`/api/Asset/DriverScoreEventDispute/${disputeId}`)
      .then((response: DriverScoreDispute | null) => {
        setDispute(response);
        // If it's pending, force user to choose a resolution path instead of defaulting to "Pending".
        setSelectedStatusId(response.DisputeStatus === DisputeStatus.Pending ? null : (response.DisputeStatus ?? null));
        setResolutionNote(response.DisputeResolutionNote || "");
        setEditingEventTypeId(executionEventTypeValues.has(response.EventType) ? response.EventType : null);
        setReasonLateOptions(response.ReasonsLate);
        setSelectedReasonLateId(0);
      })
      .catch((e) => {
        // If not problem details
        if (!e?.status) alert('Unable to load dispute');

        resetState();
      })
      .finally(() => setLoading(false));
  }, [disputeId]);

  useEffect(() => {
    loadDispute();
  }, [loadDispute]);

  const resolveDispute = (statusId: number) => {
    if (!dispute) return Promise.resolve();
    setSavingStatus(true);
    return fetchApi("/api/Asset/ResolveDriverScoreEventDispute", {
      DriverScoreEventID: dispute.DriverScoreEventID,
      DisputeStatus: statusId,
      DisputeResolutionNote: resolutionNote?.trim() || null
    }, "POST")
      .then((response: JsonResponse) => {
        if (response.Success) {
          onUpdated();
          loadDispute();
        } else {
          alert(response.ErrorMessage);
        }
      })
      .catch((e) => {
        // If not problem details
        if (!e?.status) alert("Unable to update dispute status");
      })
      .finally(() => setSavingStatus(false));
  };

  const undoEventAndResolve = () => {
    if (!dispute) return;
    setSavingEventType(true);
    const note = resolutionNote?.trim() || null;
    fetchApi("/api/Asset/UndoDriverScoreEvent", {
      DriverScoreEventID: dispute.DriverScoreEventID,
      DisputeResolutionNote: note
    }, "post")
      .then((response: JsonResponse) => {
        if (response.Success) {
          onUpdated();
          loadDispute();
        } else {
          alert(response.ErrorMessage);
        }
      })
      .catch((e) => {
        // If not problem details
        if (!e?.status) alert("Unable to remove score event");
      })
      .finally(() => setSavingEventType(false));
  };

  const updateEventTypeAndResolve = () => {
    if (!dispute || editingEventTypeId === null || dispute.EventType === editingEventTypeId) return;
    setSavingEventType(true);
    const note = resolutionNote?.trim() || null;
    fetchApi("/api/Asset/UpdateDriverScoreEventType", {
      DriverScoreEventID: dispute.DriverScoreEventID,
      EventType: editingEventTypeId,
      ReasonLate: requiresReasonLateSelection(dispute.EventType, editingEventTypeId) ? selectedReasonLateId : null,
      DisputeResolutionNote: note
    }, "POST")
      .then((response: JsonResponse) => {
        if (response.Success) {
          onUpdated();
          loadDispute();
        } else {
          alert(response.ErrorMessage);
        }
      })
      .catch((e) => {
        // If not problem details
        if (!e?.status) alert("Unable to update score event type")
      })
      .finally(() => setSavingEventType(false));
  };

  return (
    <div className="card h-100">
      <div className="card-body">
        {!disputeId && <div className="text-muted">Select a dispute to view details.</div>}
        {disputeId && loading && <div className="text-muted">Loading dispute...</div>}
        {dispute && !loading && (
          <>
            <h5 className="card-title">
              {dispute.EventTypeLabel} <span className={dispute.ScoreChange >= 0 ? "text-success" : "text-danger"}>{formatScoreChange(dispute.ScoreChange)}</span> ({dispute.ScoreBefore.toFixed(0)} → {dispute.ScoreAfter.toFixed(0)})
            </h5>
            <p className="card-text">{dispute.Description}</p>

            {dispute.OrderID && <a href={`/Order/${dispute.OrderID}`} target="_blank" rel="noopener noreferrer" className="card-link">Open Order</a>}
            {dispute.QuoteID && <a href={`/Quote/Index/${dispute.QuoteID}`} target="_blank" rel="noopener noreferrer" className="card-link">Quote # {dispute.QuoteID}</a>}
            {dispute.SamsaraSafetyEventId && <a href={`https://cloud.samsara.com/o/5004388/fleet/reports/safety/inbox_feed?uuid=${dispute.SamsaraSafetyEventId}&selectedAggregationKey=event`} target="_blank" rel="noopener noreferrer" className="card-link">View Samsara Event</a>}

            {dispute.DisputeNote && <div className="mt-3">
              <strong>Driver note:</strong>
              <div>{dispute.DisputeNote}</div>
            </div>}

            {dispute.DisputeResolutionNote && <div className="mt-3">
              <strong>Resolution note:</strong>
              <div>{dispute.DisputeResolutionNote}</div>
            </div>}

            {dispute.IsUndone && <div className="mt-3">
              <em>This event has been removed.</em>
            </div>}

            {dispute.DisputeStatus === 100 && (() => {
              const isEditableEvent = executionEventTypeValues.has(dispute.EventType);
              const isAdjustSelected = selectedStatusId === DisputeStatus.ScoreAdjusted;
              const isStandsSelected = selectedStatusId === DisputeStatus.ScoreStands;
              const busy = savingStatus || savingEventType;
              const noteIsValid = resolutionNote.trim().length > 0;

              const selectedExecutionEventOption: ExecutionEventOption | undefined = editingEventTypeId === null
                ? undefined
                : executionEventOptions.find(opt => opt.value === editingEventTypeId);
              const reasonLateRequired = requiresReasonLateSelection(dispute.EventType, editingEventTypeId);

              return (
                <>
                  <h5 className="mt-3">RESOLUTION</h5>

                  <div className="mt-2">
                    <div className="d-flex" style={{ gap: 8 }}>
                      <Button
                        themeColor={isStandsSelected ? "primary" : "base"}
                        fillMode={isStandsSelected ? "solid" : "outline"}
                        disabled={busy}
                        onClick={() => setSelectedStatusId(DisputeStatus.ScoreStands)}
                      >
                        Score Stands
                      </Button>
                      <Button
                        themeColor={isAdjustSelected ? "primary" : "base"}
                        fillMode={isAdjustSelected ? "solid" : "outline"}
                        disabled={busy}
                        onClick={() => setSelectedStatusId(DisputeStatus.ScoreAdjusted)}
                      >
                        Adjust Score
                      </Button>
                    </div>
                  </div>

                  {(isAdjustSelected || isStandsSelected) &&<div className="mt-3">
                    <div className="mt-1">
                      <TextArea
                        rows={3}
                        required
                        value={resolutionNote}
                        onChange={(e) => setResolutionNote(e.target.value)}
                        placeholder={isAdjustSelected ? "Note explaining the score adjustment..." : "Note explaining why the score stands..."}
                        disabled={busy}
                      />
                    </div>
                  </div>}

                  {isAdjustSelected && (
                    <div className="mt-3">
                      {isEditableEvent ? <>
                          <DropDownList
                            fillMode="outline"
                            data={executionEventOptions}
                            textField="label"
                            dataItemKey="value"
                            value={selectedExecutionEventOption}
                            valid={editingEventTypeId !== dispute.EventType}
                            onChange={(e) => {
                              const option = e.value as ExecutionEventOption | null;
                              setEditingEventTypeId(option ? option.value : null);
                              setSelectedReasonLateId(0);
                            }}
                            disabled={busy}
                          />
                          {reasonLateRequired && <>
                            <br /><br />
                            <DropDownList
                              fillMode="outline"
                              data={reasonLateOptions}
                              textField="Name"
                              dataItemKey="ID"
                              value={reasonLateOptions.find(x => x.ID === selectedReasonLateId)}
                              required
                              valid={selectedReasonLateId !== 0}
                              onChange={(e) => setSelectedReasonLateId(e.value?.ID ?? 0)}
                              disabled={busy}
                            />
                          </>}
                          <br /><br />
                          <Button
                            themeColor="warning"
                            disabled={!noteIsValid || editingEventTypeId === null || editingEventTypeId === dispute.EventType || (reasonLateRequired && selectedReasonLateId === 0) || busy}
                            onClick={updateEventTypeAndResolve}
                          >
                            Change Type &amp; Resolve
                          </Button>
                      </> : (
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                          <Button
                            themeColor="error"
                            disabled={!noteIsValid || busy || dispute.IsUndone}
                            onClick={undoEventAndResolve}
                          >
                            Remove Event &amp; Resolve
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {isStandsSelected && <div className="mt-3">
                    <Button
                      themeColor="primary"
                      disabled={!noteIsValid || busy}
                      onClick={() => resolveDispute(DisputeStatus.ScoreStands)}
                    >
                      Confirm Score Stands
                    </Button>
                  </div>}
                </>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
};

export default ScoreDisputeDetails;
